import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

const INPUT_TYPES = ["text", "url", "email", "select", "textarea", "number"];

const emptyInput = { name: "", type: "text", label: "", placeholder: "", required: false, active: true };
const emptySubcommand = { id: "", name: "", description: "", group: "", command: "", inputs: [] };

// Agents may carry several categories; pre-multi-category records only have
// the single `category` string.
const agentCategories = (agent) =>
  agent?.categories?.length
    ? agent.categories
    : agent?.category
      ? [agent.category]
      : [];

const labelStyle = { display: "block", fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", fontWeight: "500" };
const inputStyle = { width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-primary)", fontSize: "13px", outline: "none" };

function AgentField({ label, value, onChange, placeholder, required, disabled }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        style={{ ...inputStyle, opacity: disabled ? 0.5 : 1 }}
      />
    </div>
  );
}

// Multi-value category picker. Type and press Enter (or comma) to add,
// click × or press Backspace on an empty box to remove.
function CategoriesField({ values, onChange, suggestions }) {
  const [draft, setDraft] = useState("");

  const add = (raw) => {
    const name = raw.trim();
    if (!name) return;
    const exists = values.some((v) => v.toLowerCase() === name.toLowerCase());
    if (!exists) onChange([...values, name]);
    setDraft("");
  };

  const remove = (name) => onChange(values.filter((v) => v !== name));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault(); // don't submit the agent form
      add(draft);
    } else if (e.key === "Backspace" && !draft && values.length) {
      remove(values[values.length - 1]);
    }
  };

  const unused = suggestions.filter(
    (s) => !values.some((v) => v.toLowerCase() === s.toLowerCase()),
  );

  return (
    <div>
      <label style={labelStyle}>Categories</label>
      <div style={{ ...inputStyle, display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", padding: "6px 8px" }}>
        {values.map((cat) => (
          <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11px", padding: "3px 7px", background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.35)", borderRadius: "5px", color: "#67e8f9" }}>
            {cat}
            <button type="button" onClick={() => remove(cat)} aria-label={`Remove ${cat}`} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "13px", lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => add(draft)}
          placeholder={values.length ? "Add another..." : "SEO"}
          style={{ flex: 1, minWidth: "90px", background: "none", border: "none", outline: "none", color: "var(--text-primary)", fontSize: "13px", padding: "2px 0" }}
        />
      </div>
      {unused.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "6px" }}>
          {unused.map((s) => (
            <button key={s} type="button" onClick={() => add(s)} style={{ fontSize: "10px", padding: "2px 7px", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: "5px", color: "var(--text-muted)", cursor: "pointer" }}>
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InputRow({ input, onChange, onRemove }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: "8px", padding: "12px", marginBottom: "8px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
        <AgentField label="Field Name" value={input.name} onChange={(v) => onChange("name", v)} placeholder="url" />
        <div>
          <label style={labelStyle}>Type</label>
          <select value={input.type} onChange={(e) => onChange("type", e.target.value)} style={inputStyle}>
            {INPUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
        <AgentField label="Label" value={input.label} onChange={(v) => onChange("label", v)} placeholder="Website URL" />
        <AgentField label="Placeholder" value={input.placeholder} onChange={(v) => onChange("placeholder", v)} placeholder="https://..." />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "8px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={input.required} onChange={(e) => onChange("required", e.target.checked)} />
          Required
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--text-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={input.active} onChange={(e) => onChange("active", e.target.checked)} />
          Active
        </label>
        <button type="button" onClick={onRemove} style={{ marginLeft: "auto", fontSize: "11px", padding: "3px 8px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "5px", color: "#f87171", cursor: "pointer" }}>
          Remove
        </button>
      </div>
    </div>
  );
}

export default function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [expandedSubcmd, setExpandedSubcmd] = useState(null); // index of expanded subcommand in form
  const [savingOrder, setSavingOrder] = useState(false);

  const emptyForm = {
    id: "", name: "", icon: "", categories: [], description: "",
    command: "", estimatedTime: "~2 min", active: true,
    hasSubcommands: false, inputs: [], subcommands: [],
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.adminGetAgents();
      setAgents(res.data.agents);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load agents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Categories already in use, offered as one-click suggestions in the form
  const knownCategories = Array.from(
    new Set(agents.flatMap(agentCategories)),
  ).sort((a, b) => a.localeCompare(b));

  const openCreate = () => {
    setEditingAgent(null);
    setForm(emptyForm);
    setExpandedSubcmd(null);
    setShowForm(true);
  };

  const openEdit = (agent) => {
    setEditingAgent(agent);
    setExpandedSubcmd(null);
    setForm({
      id: agent.id,
      name: agent.name,
      icon: agent.icon || "",
      categories: agentCategories(agent),
      description: agent.description || "",
      command: agent.command || "",
      estimatedTime: agent.estimatedTime || "~2 min",
      active: agent.active !== false,
      hasSubcommands: agent.hasSubcommands || false,
      inputs: agent.inputs || [],
      subcommands: (agent.subcommands || []).map((s) => ({
        id: s.id || "",
        name: s.name || "",
        description: s.description || "",
        group: s.group || "",
        command: s.command || "",
        inputs: s.inputs || [],
      })),
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        id: form.id,
        name: form.name,
        icon: form.icon || undefined,
        categories: form.categories,
        description: form.description,
        command: form.command,
        estimatedTime: form.estimatedTime,
        active: form.active,
        hasSubcommands: form.hasSubcommands,
        inputs: form.inputs,
        subcommands: form.hasSubcommands ? form.subcommands : [],
      };
      if (editingAgent) {
        await api.adminUpdateAgent(editingAgent.id, payload);
      } else {
        await api.adminCreateAgent(payload);
      }
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save agent");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (agent) => {
    try {
      await api.adminDeleteAgent(agent.id);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to delete agent");
    }
  };

  const toggleActive = async (agent) => {
    try {
      await api.adminUpdateAgent(agent.id, { active: !agent.active });
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to update agent");
    }
  };

  // ── Ordering ──
  // The list is shown in the exact sequence users see on the dashboard.
  // Moving a row rewrites every agent's position so the order is fully
  // explicit from then on; the UI updates first and rolls back if the save
  // fails. Buttons stay disabled during a save so two moves can't interleave.
  const moveAgent = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= agents.length) return;

    const previous = agents;
    const next = [...agents];
    [next[index], next[target]] = [next[target], next[index]];
    setAgents(next);
    setSavingOrder(true);
    setError("");
    try {
      await api.adminReorderAgents(next.map((a) => a.id));
    } catch (e) {
      setAgents(previous);
      setError(e.response?.data?.error || "Failed to save agent order");
    } finally {
      setSavingOrder(false);
    }
  };

  // ── Top-level inputs helpers ──
  const addInput = () => setForm((f) => ({ ...f, inputs: [...f.inputs, { ...emptyInput }] }));
  const updateInput = (idx, field, value) =>
    setForm((f) => { const inputs = [...f.inputs]; inputs[idx] = { ...inputs[idx], [field]: value }; return { ...f, inputs }; });
  const removeInput = (idx) => setForm((f) => ({ ...f, inputs: f.inputs.filter((_, i) => i !== idx) }));

  // ── Subcommand helpers ──
  const addSubcommand = () => {
    setForm((f) => ({ ...f, subcommands: [...f.subcommands, { ...emptySubcommand, inputs: [] }] }));
    setExpandedSubcmd(form.subcommands.length); // expand the new one
  };
  const updateSubcommand = (si, field, value) =>
    setForm((f) => { const subs = [...f.subcommands]; subs[si] = { ...subs[si], [field]: value }; return { ...f, subcommands: subs }; });
  const removeSubcommand = (si) => {
    setForm((f) => ({ ...f, subcommands: f.subcommands.filter((_, i) => i !== si) }));
    setExpandedSubcmd(null);
  };
  const addSubcommandInput = (si) =>
    setForm((f) => { const subs = [...f.subcommands]; subs[si] = { ...subs[si], inputs: [...subs[si].inputs, { ...emptyInput }] }; return { ...f, subcommands: subs }; });
  const updateSubcommandInput = (si, ii, field, value) =>
    setForm((f) => { const subs = [...f.subcommands]; const inputs = [...subs[si].inputs]; inputs[ii] = { ...inputs[ii], [field]: value }; subs[si] = { ...subs[si], inputs }; return { ...f, subcommands: subs }; });
  const removeSubcommandInput = (si, ii) =>
    setForm((f) => { const subs = [...f.subcommands]; subs[si] = { ...subs[si], inputs: subs[si].inputs.filter((_, i) => i !== ii) }; return { ...f, subcommands: subs }; });

  const sectionHeader = (label) => (
    <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px", paddingBottom: "8px", borderTop: "1px solid var(--border)", paddingTop: "14px" }}>
      {label}
    </div>
  );

  return (
    <div>
      {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)", margin: 0 }}>
          Agents{agents.length > 0 && <span style={{ color: "var(--text-muted)", fontWeight: "500", fontSize: "13px" }}> ({agents.length})</span>}
        </h2>
        <button className="btn" onClick={openCreate} style={{ fontSize: "13px", padding: "7px 16px" }}>+ Create Agent</button>
      </div> */}

      {error && (
        <div style={{ padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "8px", color: "#f87171", fontSize: "13px", marginBottom: "16px" }}>
          {error}
          <button onClick={() => setError("")} style={{ float: "right", background: "none", border: "none", color: "#f87171", cursor: "pointer" }}>×</button>
        </div>
      )}

      {/* ── Agent Form Modal ── */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", zIndex: 1000, overflowY: "auto", padding: "20px" }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "28px", width: "100%", maxWidth: "600px", margin: "20px auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700" }}>{editingAgent ? "Edit Agent" : "Create Agent"}</h3>
              <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "20px" }}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

                {/* Basic fields */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <AgentField label="Agent ID *" value={form.id} onChange={(v) => setForm((f) => ({ ...f, id: v }))} placeholder="my-agent-id" required disabled={!!editingAgent} />
                  <AgentField label="Name *" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="My Agent" required />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", alignItems: "start" }}>
                  <CategoriesField values={form.categories} onChange={(v) => setForm((f) => ({ ...f, categories: v }))} suggestions={knownCategories} />
                  <AgentField label="Estimated Time" value={form.estimatedTime} onChange={(v) => setForm((f) => ({ ...f, estimatedTime: v }))} placeholder="~2 min" />
                </div>
                <AgentField label="Command *" value={form.command} onChange={(v) => setForm((f) => ({ ...f, command: v }))} placeholder="/seo audit" required />
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Describe what this agent does..." rows={2} style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                <div style={{ display: "flex", gap: "20px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--text-secondary)", cursor: "pointer" }}>
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} style={{ width: "15px", height: "15px" }} />
                    Active (visible to users)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--text-secondary)", cursor: "pointer" }}>
                    <input type="checkbox" checked={form.hasSubcommands} onChange={(e) => setForm((f) => ({ ...f, hasSubcommands: e.target.checked }))} style={{ width: "15px", height: "15px" }} />
                    Has Subcommands
                  </label>
                </div>

                {/* Top-level Inputs (shown when no subcommands) */}
                {!form.hasSubcommands && (
                  <div>
                    {sectionHeader("Inputs")}
                    {form.inputs.map((input, idx) => (
                      <InputRow key={idx} input={input} onChange={(field, val) => updateInput(idx, field, val)} onRemove={() => removeInput(idx)} />
                    ))}
                    <button type="button" onClick={addInput} style={{ fontSize: "12px", padding: "5px 12px", background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.25)", borderRadius: "6px", color: "var(--teal-bright)", cursor: "pointer" }}>
                      + Add Input
                    </button>
                  </div>
                )}

                {/* Subcommands */}
                {form.hasSubcommands && (
                  <div>
                    {sectionHeader(`Subcommands (${form.subcommands.length})`)}
                    {form.subcommands.map((sub, si) => (
                      <div key={si} style={{ border: "1px solid var(--border)", borderRadius: "10px", marginBottom: "10px", overflow: "hidden" }}>
                        {/* Subcommand header row */}
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: "rgba(255,255,255,0.03)", cursor: "pointer" }} onClick={() => setExpandedSubcmd(expandedSubcmd === si ? null : si)}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: "var(--text-muted)", transform: expandedSubcmd === si ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
                            <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)", flex: 1 }}>
                            {sub.name || <span style={{ color: "var(--text-muted)", fontWeight: "400" }}>Unnamed subcommand</span>}
                            {sub.id && <code style={{ fontSize: "11px", marginLeft: "8px", color: "var(--text-muted)", background: "rgba(255,255,255,0.06)", padding: "1px 5px", borderRadius: "4px" }}>{sub.id}</code>}
                          </span>
                          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{sub.inputs?.length || 0} input{sub.inputs?.length !== 1 ? "s" : ""}</span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); removeSubcommand(si); }} style={{ fontSize: "11px", padding: "2px 8px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "5px", color: "#f87171", cursor: "pointer" }}>
                            Remove
                          </button>
                        </div>

                        {/* Subcommand fields */}
                        {expandedSubcmd === si && (
                          <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                              <AgentField label="Subcommand ID *" value={sub.id} onChange={(v) => updateSubcommand(si, "id", v)} placeholder="subcommand-id" />
                              <AgentField label="Name *" value={sub.name} onChange={(v) => updateSubcommand(si, "name", v)} placeholder="Subcommand Name" />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                              <AgentField label="Group" value={sub.group} onChange={(v) => updateSubcommand(si, "group", v)} placeholder="Optional group" />
                              <AgentField label="Command *" value={sub.command} onChange={(v) => updateSubcommand(si, "command", v)} placeholder="/skill subcommand" />
                            </div>
                            <div>
                              <label style={labelStyle}>Description</label>
                              <input type="text" value={sub.description} onChange={(e) => updateSubcommand(si, "description", e.target.value)} placeholder="What this subcommand does" style={inputStyle} />
                            </div>

                            {/* Subcommand inputs */}
                            <div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Inputs ({sub.inputs.length})</span>
                                <button type="button" onClick={() => addSubcommandInput(si)} style={{ fontSize: "11px", padding: "3px 10px", background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.25)", borderRadius: "5px", color: "var(--teal-bright)", cursor: "pointer" }}>
                                  + Add Input
                                </button>
                              </div>
                              {sub.inputs.map((inp, ii) => (
                                <InputRow key={ii} input={inp} onChange={(field, val) => updateSubcommandInput(si, ii, field, val)} onRemove={() => removeSubcommandInput(si, ii)} />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addSubcommand} style={{ fontSize: "12px", padding: "5px 12px", background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.25)", borderRadius: "6px", color: "var(--teal-bright)", cursor: "pointer" }}>
                      + Add Subcommand
                    </button>
                  </div>
                )}

                {error && <div style={{ color: "#f87171", fontSize: "12px" }}>{error}</div>}

                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", paddingTop: "4px" }}>
                  <button type="button" onClick={() => setShowForm(false)} className="btn" style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)" }}>Cancel</button>
                  <button type="submit" className="btn" disabled={saving} style={{ minWidth: "80px" }}>{saving ? "Saving..." : editingAgent ? "Update" : "Create"}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "28px", width: "100%", maxWidth: "380px" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: "15px", fontWeight: "700" }}>Deactivate Agent?</h3>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "0 0 20px" }}>
              This will deactivate <strong style={{ color: "var(--text-primary)" }}>{deleteConfirm.name}</strong>. The agent will no longer be visible to users.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setDeleteConfirm(null)} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)" }}>Cancel</button>
              <button className="btn" onClick={() => handleDelete(deleteConfirm)} style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171" }}>Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Agent list ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>Loading...</div>
      ) : agents.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)", fontSize: "13px" }}>No agents found</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "2px" }}>
            Agents appear on the dashboard in this order. Use ↑ / ↓ to change it — changes save instantly.
            {savingOrder && <span style={{ color: "var(--teal-bright)", marginLeft: "8px" }}>Saving order…</span>}
          </div>
          {agents.map((agent, index) => (
            <div key={agent.id} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => moveAgent(index, -1)}
                    disabled={index === 0 || savingOrder}
                    title="Move up"
                    aria-label={`Move ${agent.name} up`}
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: "5px", color: "var(--text-muted)", cursor: index === 0 || savingOrder ? "default" : "pointer", opacity: index === 0 || savingOrder ? 0.3 : 1, fontSize: "10px", lineHeight: 1, padding: "3px 6px" }}
                  >▲</button>
                  <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: "700" }}>{index + 1}</span>
                  <button
                    type="button"
                    onClick={() => moveAgent(index, 1)}
                    disabled={index === agents.length - 1 || savingOrder}
                    title="Move down"
                    aria-label={`Move ${agent.name} down`}
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: "5px", color: "var(--text-muted)", cursor: index === agents.length - 1 || savingOrder ? "default" : "pointer", opacity: index === agents.length - 1 || savingOrder ? 0.3 : 1, fontSize: "10px", lineHeight: 1, padding: "3px 6px" }}
                  >▼</button>
                </div>
                <div style={{ width: "34px", height: "34px", borderRadius: "8px", background: agent.active !== false ? "rgba(6,182,212,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${agent.active !== false ? "rgba(6,182,212,0.3)" : "var(--border)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", flexShrink: 0 }}>
                  {agent.icon || agent.name.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: "120px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }}>{agent.name}</span>
                    {agentCategories(agent).map((cat) => (
                      <span key={cat} style={{ fontSize: "10px", color: "var(--text-muted)", padding: "2px 7px", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: "5px" }}>{cat}</span>
                    ))}
                    <span style={{ fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "5px", background: agent.active !== false ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${agent.active !== false ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, color: agent.active !== false ? "#4ade80" : "#f87171", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {agent.active !== false ? "Active" : "Inactive"}
                    </span>
                    {agent.hasSubcommands && (
                      <span style={{ fontSize: "10px", padding: "2px 7px", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: "5px", color: "#a78bfa" }}>
                        {agent.subcommands?.length || 0} subcommand{agent.subcommands?.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                    <code style={{ fontSize: "11px", background: "rgba(255,255,255,0.05)", padding: "1px 5px", borderRadius: "4px" }}>{agent.id}</code>
                    {" · "}{agent.estimatedTime}
                    {!agent.hasSubcommands && agent.inputs?.length > 0 && ` · ${agent.inputs.length} input${agent.inputs.length > 1 ? "s" : ""}`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  <button className="btn btn-small" onClick={() => setExpandedAgent(expandedAgent === agent.id ? null : agent.id)} style={{ fontSize: "12px", padding: "5px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>
                    {expandedAgent === agent.id ? "Less" : "More"}
                  </button>
                  <button className="btn btn-small" onClick={() => toggleActive(agent)} style={{ fontSize: "12px", padding: "5px 10px", background: agent.active !== false ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)", border: `1px solid ${agent.active !== false ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`, color: agent.active !== false ? "#f87171" : "#4ade80" }}>
                    {agent.active !== false ? "Disable" : "Enable"}
                  </button>
                  <button className="btn btn-small" onClick={() => openEdit(agent)} style={{ fontSize: "12px", padding: "5px 12px", background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>Edit</button>
                  <button className="btn btn-small" onClick={() => setDeleteConfirm(agent)} style={{ fontSize: "12px", padding: "5px 12px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>Delete</button>
                </div>
              </div>

              {expandedAgent === agent.id && (
                <div style={{ padding: "12px 16px 14px", borderTop: "1px solid var(--border)", background: "rgba(255,255,255,0.02)" }}>
                  {agent.description && <p style={{ fontSize: "12px", color: "var(--text-muted)", margin: "0 0 10px" }}>{agent.description}</p>}
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "8px" }}>
                    <strong style={{ color: "var(--text-secondary)" }}>Command:</strong>{" "}
                    <code style={{ background: "rgba(255,255,255,0.05)", padding: "2px 6px", borderRadius: "4px" }}>{agent.command}</code>
                  </div>

                  {/* Top-level inputs */}
                  {!agent.hasSubcommands && agent.inputs?.length > 0 && (
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontSize: "11px", fontWeight: "600", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>Inputs</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {agent.inputs.map((inp, i) => (
                          <span key={i} style={{ fontSize: "11px", padding: "3px 8px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: "5px", color: "var(--text-muted)" }}>
                            {inp.label || inp.name} <span style={{ opacity: 0.6 }}>({inp.type}){inp.required ? " *" : ""}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subcommands */}
                  {agent.hasSubcommands && agent.subcommands?.length > 0 && (
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: "600", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Subcommands</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {agent.subcommands.map((sub, i) => (
                          <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: "8px", padding: "10px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: sub.inputs?.length ? "6px" : 0 }}>
                              <code style={{ fontSize: "11px", background: "rgba(255,255,255,0.06)", padding: "1px 5px", borderRadius: "4px", color: "var(--teal-bright)" }}>{sub.id}</code>
                              <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)" }}>{sub.name}</span>
                              {sub.group && <span style={{ fontSize: "10px", color: "var(--text-muted)", padding: "1px 6px", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: "4px" }}>{sub.group}</span>}
                              <code style={{ fontSize: "11px", color: "var(--text-muted)", marginLeft: "auto" }}>{sub.command}</code>
                            </div>
                            {sub.description && <p style={{ fontSize: "11px", color: "var(--text-muted)", margin: "0 0 6px" }}>{sub.description}</p>}
                            {sub.inputs?.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                {sub.inputs.map((inp, j) => (
                                  <span key={j} style={{ fontSize: "10px", padding: "2px 6px", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: "4px", color: "var(--text-muted)" }}>
                                    {inp.label || inp.name}{inp.required ? " *" : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
